import React from 'react';
import styled from 'styled-components';
import NotificationsNoneOutlinedIcon from '@material-ui/icons/NotificationsNoneOutlined';
import { Badge, Button, Menu, MenuItem, Typography } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import T from 'i18n-react';
import { makeStyles } from '@material-ui/core/styles';
import useIsMounted from 'ismounted';
import Skeleton from '@material-ui/lab/Skeleton';
import {
  onChangeProp,
  onErrorProp,
  Unsubscribe,
} from '../firebase/observeUserAuthChange';
import ComparedToToday from './ComparedToToday';
import {
  getNotifications,
  listenNotifications,
  listenNotificationsCount,
  readNotifications,
} from './firestore';
import orderBy from 'lodash/orderBy';
import { Link, useLocation } from 'react-router-dom';
import MuiIconWithWord from '../../front/components/Header/molecules/MuiIconWithWord';
import { getNotificationLink } from './utils/getNotificationLink';

const IconWrapper = styled.div`
  cursor: pointer;
`;

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    width: '100%',
    [theme.breakpoints.up(300)]: {
      width: 300,
    },
  },
  menuHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
  },
  menuHeaderTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'bold',
  },
  icon: {
    color: theme.palette.primary.main,
  },
  badgeBadge: {
    top: '2px',
    right: '10px',

    [theme.breakpoints.up('md')]: {
      top: '-10px',
      left: '10px',
      right: 'unset',
      transform: 'none',
    },
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.primary,
    whiteSpace: 'normal',
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.text.primary,
      textDecoration: 'none',
    },
  },
  menuItemSubject: {
    padding: theme.spacing(0, 1, 0.5, 0),
  },
}));

type NotificationsProps = {
  col: 'businesses' | 'users',
  router?: boolean,
  observeUserAuthChange: (
    onChange: onChangeProp,
    onError: onErrorProp,
  ) => Unsubscribe,
  onUserChange?: (user: Object, setUid: (uid: string) => void) => void,
  classes?: { [key: string]: string },
  disabled?: boolean,
  accessibleSiteIds?: number[] | null,
};

const LIMIT = 50;

const Notifications = ({
  classes: classesProps,
  col,
  observeUserAuthChange,
  onUserChange,
  disabled = false,
  router = false,
  accessibleSiteIds = null,
}: NotificationsProps) => {
  const classes = useStyles({ classes: classesProps });

  const isMounted = useIsMounted();

  const [state, setState] = React.useState({
    count: 0,
    user: null,
    uid: null,
    notifications: [],
    moreNotifications: [],
    loaded: false,
    loading: false,
    clicked: false,
    anchorEl: null,
    next: false,
  });

  const {
    count,
    user,
    uid,
    notifications,
    moreNotifications,
    loaded,
    loading,
    clicked,
    anchorEl,
    next,
  } = state;

  const allNotifications = [...notifications, ...moreNotifications];

  // accessibleSiteIds が null の場合は全件表示、配列の場合は businessSiteId でフィルタ
  const isAccessibleNotification = (snap) => {
    if (!accessibleSiteIds) return true;
    const siteId = snap.data()?.businessSiteId;
    return siteId == null || accessibleSiteIds.includes(siteId);
  };

  const visibleNotifications = allNotifications.filter(
    isAccessibleNotification,
  );

  const updateState = (data = {}) =>
    setState((prevState) => ({ ...prevState, ...data }));

  const handleClick = (event) =>
    updateState({ anchorEl: event.currentTarget, clicked: true });

  const handleClose = () => updateState({ anchorEl: null });

  const handleMarkAllAsRead = () => {
    const unreadSnaps = visibleNotifications.filter((n) => !n.data()?.isRead);
    if (unreadSnaps.length === 0) return;
    readNotifications(col, uid, unreadSnaps);
  };

  const handleMarkAsRead = (snapshot) => (event) =>
    readNotifications(col, uid, [snapshot]);

  const handleScrollMenu = (e) => {
    if (!loaded || loading) return;

    const { offsetHeight, scrollHeight, scrollTop } = e.currentTarget;
    if (next && uid && scrollHeight === offsetHeight + scrollTop) {
      e.preventDefault();

      updateState({ loading: true });

      getNotifications(col, uid, notifications[notifications.length - 1], LIMIT)
        .then((notifications) => {
          if (isMounted.current) {
            setState((prevState) => {
              const moreNotifications = [
                ...prevState.moreNotifications,
                ...notifications,
              ];
              return {
                ...prevState,
                loading: false,
                next: notifications.length >= LIMIT,
                moreNotifications,
              };
            });
          }
        })
        .catch(() => {});
    }
  };

  React.useEffect(() => {
    return observeUserAuthChange((user) => {
      if (isMounted.current && user) {
        const setUid = (uid) => isMounted.current && updateState({ uid, user });
        onUserChange(user, setUid);
      }
    });
  }, []);

  React.useEffect(() => {
    if (uid) {
      const handleCountChange = (count) => {
        if (isMounted.current) {
          const displayCount = Math.max(count || 0, 0);
          updateState({ loaded: true, count: displayCount });
        }
      };

      return listenNotificationsCount(
        col,
        uid,
        handleCountChange,
        accessibleSiteIds,
      );
    }
  }, [user]);

  // 通知一覧が読み込まれたら、表示対象の未読数でバッジカウントを更新
  React.useEffect(() => {
    if (clicked) {
      const unreadCount = visibleNotifications.filter(
        (n) => !n.data()?.isRead,
      ).length;
      updateState({ count: unreadCount });
    }
  }, [notifications, moreNotifications]);

  if (router) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const location = useLocation();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(handleClose, [location]);
  }

  React.useEffect(() => {
    if (clicked) {
      updateState({ loading: true });

      const handleNotificationsChange = (snapshot) =>
        setTimeout(() => {
          if (isMounted.current) {
            setState((prevState) => {
              const notifications = [...prevState.notifications];

              snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                  notifications.push(change.doc);
                } else if (change.type === 'modified') {
                  const idx = notifications.findIndex(
                    (n) => n.id === change.doc.id,
                  );
                  idx > -1 && (notifications[idx] = change.doc);
                } else if (change.type === 'removed') {
                  const idx = notifications.findIndex(
                    (n) => n.id === change.doc.id,
                  );
                  idx > -1 && notifications.splice(idx, 1);
                }
              });

              return {
                ...prevState,
                loading: false,
                next: notifications.length >= LIMIT,
                notifications: orderBy(
                  notifications,
                  [(n) => n.data()?.createdAt?.toDate?.()],
                  ['desc'],
                ),
              };
            });
          }
        }, 300);

      return listenNotifications(col, uid, handleNotificationsChange, LIMIT);
    }
  }, [clicked, user]);

  return (
    <>
      <IconWrapper {...(loaded && { onClick: handleClick })}>
        <Badge
          badgeContent={count}
          color="primary"
          max={99}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          classes={{ badge: classes.badgeBadge }}
        >
          <MuiIconWithWord
            Icon={NotificationsNoneOutlinedIcon}
            word={T.translate('header.notification')}
            color={col === 'businesses' ? 'white' : undefined}
          />
        </Badge>
      </IconWrapper>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        getContentAnchorEl={null}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          onScroll: handleScrollMenu,
          className: classes.menuPaper,
        }}
      >
        {loaded && (
          <div className={classes.menuHeader}>
            <Typography className={classes.menuHeaderTitle}>
              {T.translate('user.notification.title')}
            </Typography>
            <Button
              size="small"
              onClick={handleMarkAllAsRead}
              disabled={visibleNotifications.length === 0}
            >
              {T.translate('user.notification.markAsRead')}
            </Button>
          </div>
        )}
        {loaded &&
          visibleNotifications.map((snapshot, index) => {
            const { content, url, isRead, createdAt } = snapshot.data();
            const href = url ? getNotificationLink(url) : null;

            return (
              <MenuItem
                key={`notification-item${index}`}
                {...(router
                  ? {
                      component: Link,
                      to: href,
                    }
                  : {
                      component: 'a',
                      href,
                    })}
                className={classes.menuItem}
                onClick={handleMarkAsRead(snapshot)}
              >
                <div style={{ width: '100%' }}>
                  <Typography
                    className={classes.menuItemSubject}
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                  <ComparedToToday date={createdAt.toDate()} />
                </div>
                <FiberManualRecordIcon
                  color="primary"
                  fontSize="small"
                  style={{ color: isRead && 'transparent' }}
                />
              </MenuItem>
            );
          })}
        {(loaded || !loading) && visibleNotifications.length === 0 && (
          <MenuItem disabled>
            {T.translate('headerNotifications.noNotification')}
          </MenuItem>
        )}
        {loading &&
          [...Array(Math.max(count, 1)).keys()].map((idx) => (
            <MenuItem key={`loading-item${idx}`}>
              <Skeleton variant="text" width="100%" />
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default Notifications;
